import {
  ArrowRightIcon,
  CalendarAltIcon,
  LocationAltIcon,
  PhoneIcon,
  RmxEditIcon
} from 'cokitchen-icons';
import moment from 'moment';
import React, { useEffect, useState, useRef } from 'react';
import {
  formatCurrency,
  formatDeliveryTime,
  handleApiError,
  truncateString
} from 'utils/functions';
import { ReactComponent as CutleryIcon } from 'assets/icons/cutlery.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/question-icon-filled.svg';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import ScheduledDeliveryModal from 'components/modals/scheduled-delivery-modal';
import CancelOrderModal from 'components/modals/cancel-order-modal';
import AppreciationModal from 'components/modals/appreciation-note-modal';
import AddNewNumberModal from 'components/modals/add-new-number';
import useDashboardContext from 'contexts/dashboard.context';
import GroupOrderMealAccordion from 'components/meal-accordion/group-order-meal-accordion';
import BulkOrderMealAccordion from 'components/meal-accordion/bulk-order-meal-accordion';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import useOrdersContext from 'contexts/orders.context';
import UpdateOrderSuccessModal from 'components/modals/update-order-success-modal';
import { OrderType } from 'models/order.model';

type Props = {
  order: OrderType;
};

const EditOrderDetails = ({ order }: Props) => {
  const { setBackLinkTitle, setIsBackLink } = useDashboardContext();
  const { calculatedOrderForm, setcalculatedOrderForm, createOrderForm, setCreateOrderForm } =
    useOrdersContext();
  const [showScheduledDeliveryModal, setShowScheduledDeliveryModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showAppreciationNoteModal, setShowAppreciationNoteModal] = useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [editDone, setEditDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const deliveryDate =
    order.calculated_order.scheduled_delivery_date &&
    order.calculated_order.scheduled_delivery_date.split('T');
  const deliveryTime = order.calculated_order.scheduled_delivery_time ?? '';
  const [updateDeliveryTime, setUpdateDeliveryTime] = useState(deliveryTime);
  const [updateDeliveryDate, setUpdateDeliveryDate] = useState(deliveryDate[0]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [updateAppreciationNote, setUpdateAppreciationNote] = useState(
    order?.order_details.appreciation_note ?? ''
  );
  const [phoneNumber, setPhoneNumber] = useState(
    order.calculated_order.address_details.phone_number
  );

  const phoneNumberRef = useRef<null | HTMLInputElement>(null);
  const handleInputFocus = () => {
    if (phoneNumberRef) phoneNumberRef?.current?.focus();
  };

  useEffect(() => {
    setcalculatedOrderForm({
      ...calculatedOrderForm,
      scheduled_delivery_date: deliveryDate[0],
      scheduled_delivery_time: deliveryTime
    });
    setCreateOrderForm({
      ...createOrderForm,
      order_details: {
        ...createOrderForm.order_details,
        appreciation_note: updateAppreciationNote
      }
    });
  }, [order.calculated_order]);

  const subTotal =
    Number(order?.calculated_order?.total_amount) -
    Number(order?.calculated_order?.delivery_fee) -
    Number(order?.calculated_order?.delivery_service_charge) -
    Number(order?.calculated_order.service_charge);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);
  useEffect(() => {
    if (
      calculatedOrderForm.scheduled_delivery_date &&
      calculatedOrderForm.scheduled_delivery_time &&
      editDone
    ) {
      const deliveryDateTime = calculatedOrderForm.scheduled_delivery_time.split('T');
      const deliveryTime = deliveryDateTime[0]?.split('.')[0];
      setUpdateDeliveryDate(calculatedOrderForm.scheduled_delivery_date);
      setUpdateDeliveryTime(deliveryTime);
    }
  }, [calculatedOrderForm.scheduled_delivery_time, calculatedOrderForm.scheduled_delivery_date]);

  useEffect(() => {
    if (createOrderForm.order_details.appreciation_note !== '') {
      setUpdateAppreciationNote(createOrderForm.order_details.appreciation_note);
    }
  }, [createOrderForm.order_details.appreciation_note]);

  useEffect(() => {
    if (
      updateAppreciationNote !== createOrderForm.order_details.appreciation_note &&
      createOrderForm.order_details.appreciation_note !== ''
    ) {
      setEditDone(true);
    }
  }, [createOrderForm.order_details.appreciation_note, updateAppreciationNote, editDone]);

  useEffect(() => {
    const dateIsSame = moment(updateDeliveryDate).isSame(
      calculatedOrderForm.scheduled_delivery_date
    );
    const timeIsSame = updateDeliveryTime === calculatedOrderForm.scheduled_delivery_time;
    if (!dateIsSame && !timeIsSame) {
      setEditDone(true);
    }
  }, [updateDeliveryDate, updateDeliveryTime, editDone]);

  const updateDeliveryDateTime = async () => {
    if (!editDone) return;
    setLoading(true);
    try {
      const res = await apiService.put(`scheduled-order`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          scheduled_delivery_date: updateDeliveryDate,
          scheduled_delivery_time: updateDeliveryTime,
          order_id: order.id
        }
      });
      if (res.data.data) {
        setUpdateDeliveryDate(updateDeliveryDate);
        setUpdateDeliveryTime(updateDeliveryTime);
        toast(<ToastMessage type='success' message={'Updated successfully'} />);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const updateDeliveryDetails = async () => {
    if (!editDone) return;
    setLoading(true);
    try {
      const data: Record<string, unknown> = {
        order_code: order.order_code,
        address_phone_number: phoneNumber
      };
      if (updateAppreciationNote) data.appreciation_note = updateAppreciationNote;

      const res = await apiService.post(`order/update-rider-note-and-address`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data
      });
      if (res.data) {
        setPhoneNumber(order.calculated_order.address_details.phone_number);
        setUpdateAppreciationNote(order?.calculated_order?.appreciation_note ?? '');
        setShowSuccessModal(true);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=''>
      <div className='w-full'>
        <div className='bg-[#F7F7F8] w-full h-[85vh] lg:h-[85vh] rounded-xl p-1 flex flex-col-reverse lg:flex-row gap-2 lg:gap-6 lg:justify-between basket'>
          <div className='w-full lg:w-[48.5%] h-full relative'>
            <div className='w-full h-[30vh] pb-12 mt-[-2rem] lg:mt-0 lg:pb-0 lg:h-[80%] overflow-y-scroll no-scrollbar'>
              <div className='w-full bg-white pt-7 px-6 pb-4 rounded-tl-lg mb-3'>
                <h4 className='mb-3 font-semibold text-[#2E3031] text-2xl'>Delivery details</h4>
                <div
                  className='w-full flex justify-between items-center pb-3 border-b-[0.7px] border-b-[#EAEBEB]'
                  onClick={() => setShowScheduledDeliveryModal(true)}>
                  <div className='flex gap-2 text-gray-9 items-center'>
                    <CalendarAltIcon color='#96999C' />
                    <div>
                      <p className='text-gray-10'>
                        {moment(updateDeliveryDate).format('ddd, MMM DD')},{' '}
                        {formatDeliveryTime(updateDeliveryTime)}
                      </p>
                    </div>
                  </div>
                  <div className='w-7 h-7 rounded-full bg-[#F7F7F8] flex justify-center items-center text-gray-6'>
                    <ArrowRightIcon color='#96999C' className='cursor-pointer' />
                  </div>
                </div>
                <div className='w-full pb-3 border-b-[0.7px] border-b-[#EAEBEB] items-center mt-4 text-gray-6'>
                  <div className='w-[85%] flex gap-2 text-gray-9 items-center'>
                    <LocationAltIcon color='#96999C' />
                    <div className='w-[95%]'>
                      <p className='text-gray-6 line-clamp-1'>
                        {order.calculated_order.address_details.address_line},{' '}
                        {order.calculated_order.address_details.city}
                      </p>
                    </div>
                  </div>
                </div>
                <div className='w-full flex justify-between items-center mt-4 text-gray-6'>
                  <div className='flex gap-2 text-gray-9 items-center'>
                    <PhoneIcon color='#96999C' />
                    <input
                      type='tel'
                      maxLength={11}
                      value={phoneNumber}
                      ref={phoneNumberRef}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        if (phoneNumber.length === 11) {
                          setEditDone(true);
                        }
                      }}
                      className='text-gray-6 outline-none'
                    />
                  </div>
                  <RmxEditIcon className='cursor-pointer' onClick={handleInputFocus} />
                </div>
              </div>
              <div className='w-full py-5 px-6 flex justify-between items-center bg-white gap-4 mb-3'>
                <div className='flex gap-2'>
                  <CutleryIcon />

                  <p className='text-[#2E3031] text-sm'>Cutlery included</p>
                </div>
                <p className='text-[#96999C] text-sm font-medium'>
                  {order.order_details.cutlery ? 'yes' : 'No'}
                </p>
              </div>
              <div className='w-full py-5 px-6 bg-white mb-3 '>
                <div className='w-full mt-4 flex justify-between'>
                  <p className='text-[#6F7376] text-sm'>
                    {updateAppreciationNote !== null && updateAppreciationNote !== ''
                      ? truncateString(updateAppreciationNote ?? '', 4)
                      : 'Appreciation note'}
                  </p>
                  <p
                    className='text-red-5 text-sm font-medium cursor-pointer'
                    onClick={() => setShowAppreciationNoteModal(true)}>
                    {order?.calculated_order.appreciation_note !== '' ? 'Edit' : 'Add'}
                  </p>
                </div>
                <div className='w-full mt-4 flex justify-between'>
                  <p className='text-[#6F7376] text-sm'>Discount code</p>
                  <p className='text-[#96999C] text-sm font-medium'>
                    {order.calculated_order.discount_code === '' ? 'None' : 'Applied'}
                  </p>
                </div>
              </div>

              <div className='w-full py-4 px-6 bg-white mb-3'>
                <div className='flex justify-between mb-3'>
                  <p className='text-[#6F7376] text-sm'>Subtotal</p>
                  <p className='text-[#6F7376] text-sm font-medium'>{formatCurrency(subTotal)}</p>
                </div>
                <div className='flex justify-between mb-3'>
                  <p className='text-[#6F7376] text-sm'>Delivery fee</p>
                  <p className='text-[#6F7376] text-sm font-medium'>
                    {formatCurrency(order?.calculated_order.delivery_fee)}
                  </p>
                </div>
                <div className='flex justify-between mb-3'>
                  <div className='flex gap-1 items-center'>
                    <p className='text-[#6F7376] text-sm'>Service fee</p>
                    <QuestionIcon className='question-icon' />
                  </div>
                  <p className='text-[#6F7376] text-sm font-medium'>
                    {formatCurrency(order?.calculated_order?.service_charge)}
                  </p>
                </div>
              </div>
            </div>
            <div className='w-full rounded-bl-lg bg-white px-6 pt-6 pb-8 absolute bottom-0 left-0 total-box'>
              <div className='flex justify-between mb-3'>
                <p className='text-[#6F7376] text-sm'>Order total</p>
                <p className='text-[#2E3031] text-sm font-medium'>
                  {formatCurrency(order?.calculated_order?.total_amount)}
                </p>
              </div>

              <Button
                variant='block'
                color='primary'
                className='w-full h-[53px] !max-w-full'
                disabled={!editDone}
                loading={loading}
                onClick={updateDeliveryDetails}>
                Confirm Order
              </Button>
            </div>
          </div>
          <div className='w-full lg:w-[48.5%] h-full overflow-y-scroll no-scrollbar'>
            <div className='w-full pt-7 px-6 pb-2 bg-white rounded-tr-lg'>
              <div className='w-full flex justify-between'>
                <h4 className='mb-3 font-semibold text-[#2E3031] text-xl'>Order details</h4>
                <div className='cursor-pointer' onClick={() => setShowCancelOrderModal(true)}>
                  <p className='text-red-5 text-sm font-medium'>Cancel order</p>
                </div>
              </div>
            </div>

            <div className='w-full bg-white py-[10px] min-h-full'>
              <div className='w-full flex flex-col space-y-4'>
                {order.calculated_order.is_company_group_order &&
                order.calculated_order.is_group_order
                  ? order.calculated_order.sub_calculated_order_ids?.map((meal, index) => (
                      <GroupOrderMealAccordion meal={meal} key={index} />
                    ))
                  : order.calculated_order?.meals.map((meal, index) => (
                      <BulkOrderMealAccordion meal={meal} key={index} />
                    ))}
              </div>
            </div>
          </div>
        </div>
        <ScheduledDeliveryModal
          handleClick={(k) => {
            setcalculatedOrderForm({ ...calculatedOrderForm, ...k });
            setShowScheduledDeliveryModal(false);
            setEditDone(true);
            updateDeliveryDateTime();
          }}
          isOpen={showScheduledDeliveryModal}
          onClose={() => setShowScheduledDeliveryModal(false)}
          type='order_details'
        />

        <CancelOrderModal
          show={showCancelOrderModal}
          onClose={() => setShowCancelOrderModal(false)}
          orderCode={order.order_code}
        />

        <AppreciationModal
          show={showAppreciationNoteModal}
          onClose={() => setShowAppreciationNoteModal(false)}
        />
        <AddNewNumberModal
          show={showPhoneNumberModal}
          onClose={() => setShowPhoneNumberModal(false)}
        />
        <UpdateOrderSuccessModal
          show={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
        />
      </div>
    </div>
  );
};

export default EditOrderDetails;
